import { ServiceBanner } from './PageBanners';
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from './SEOHead';
import { Button } from './ui/button';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { DIENSTEN } from '../data/diensten';

export default function DienstTemplate({ dienst }) {
  const Icon = dienst.icon;
  const related = DIENSTEN.filter(d => d.id !== dienst.id).slice(0, 3);

  return (
    <>
      <SEOHead
        title={dienst.metaTitle}
        description={dienst.metaDescription}
        keywords={dienst.keywords}
        canonical={dienst.route}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": dienst.title,
          "description": dienst.metaDescription,
          "provider": {
            "@type": "LocalBusiness",
            "name": "IMPERIA AI",
            "url": "https://www.imperiaai.nl"
          }
        }}
      />

      {/* ── Hero (ServiceBanner) ──────────────────────────── */}
      <ServiceBanner
        badge={dienst.title}
        title={dienst.h1.split('—')[0] || dienst.title}
        titleHighlight={dienst.h1.includes('—') ? dienst.h1.split('—')[1]?.trim() : ''}
        subtitle={dienst.intro}
        primaryCta="Offerte aanvragen"
        primaryHref={`/offerte?dienst=${encodeURIComponent(dienst.title)}&type=eenmalig`}
        services={dienst.services}
      />
      <div style={{ display: 'none' }}>
      </div>

      {/* ── Probleem ──────────────────────────────────────────────────── */}
      <section className="bg-[#f8fafc] py-16">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4A017] mb-3 block">Het probleem</span>
              <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-6">{dienst.problem.title}</h2>
              <ul className="space-y-3">
                {dienst.problem.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-500 text-xs font-bold">✗</span>
                    </div>
                    <span className="text-[#64748b]">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0f172a] rounded-2xl p-8">
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4A017] mb-3 block">Onze oplossing</span>
              <h3 className="font-serif text-2xl font-bold text-white mb-4">{dienst.solution.title}</h3>
              <p className="text-[#94a3b8] leading-relaxed">{dienst.solution.text}</p>
              <Link to={`/offerte?dienst=${encodeURIComponent(dienst.title)}&type=eenmalig`} className="inline-block mt-6">
                <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold">
                  Offerte aanvragen <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Aanpak / Stappen ─────────────────────────────────────────── */}
      <section className="bg-white py-16">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-[#D4A017] mb-3 block">Onze aanpak</span>
            <h2 className="font-serif text-3xl font-bold text-[#0f172a]">Hoe wij te werk gaan</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {dienst.steps.map((s) => (
              <div key={s.n} className="relative">
                {s.n < dienst.steps.length && (
                  <div className="hidden lg:block absolute top-6 left-full w-full h-0.5 bg-[#D4A017]/20 z-0" style={{ width: 'calc(100% - 48px)', left: '48px' }} />
                )}
                <div className="bg-[#f8fafc] rounded-2xl p-6 relative z-10 h-full">
                  <div className="w-12 h-12 bg-[#D4A017] rounded-xl flex items-center justify-center mb-4">
                    <span className="text-[#0f172a] font-bold text-lg">{s.n}</span>
                  </div>
                  <h3 className="font-bold text-[#0f172a] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#64748b] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Voordelen ─────────────────────────────────────────────────── */}
      <section className="bg-[#D4A017] py-16">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#0f172a]/60 mb-3 block">Voordelen</span>
              <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-6">
                Waarom kiezen voor IMPERIA AI?
              </h2>
              <ul className="space-y-3">
                {dienst.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0f172a] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#D4A017]" />
                    </div>
                    <span className="text-[#0f172a] font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              {[
                { v: '238+', l: 'Tevreden klanten' },
                { v: '€18M+', l: 'Gegenereerde omzet' },
                { v: '95%', l: 'Klantretentie' },
                { v: '8+', l: 'Jaar ervaring' },
              ].map(({ v, l }) => (
                <div key={l} className="bg-white/30 rounded-xl px-6 py-4 flex items-center gap-4">
                  <span className="text-2xl font-bold text-[#0f172a]">{v}</span>
                  <span className="text-[#0f172a]/70 text-sm">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA + Mini offerte ──────────────────────────────────────── */}
      <section className="bg-[#0f172a] py-16">
        <div className="container-custom max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Klaar om te beginnen?
          </h2>
          <p className="text-[#94a3b8] mb-8 text-lg">
            Vraag een vrijblijvende offerte aan en ontvang binnen 24 uur een voorstel op maat.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to={`/offerte?dienst=${encodeURIComponent(dienst.title)}&type=eenmalig`}>
              <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-8 py-5 text-lg">
                Offerte aanvragen <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/prijzen">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-5 text-lg">
                Bekijk prijzen
              </Button>
            </Link>
            <Link to="/diensten">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-5 text-lg">
                Alle diensten
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Gerelateerde diensten ─────────────────────────────────────── */}
      <section className="bg-[#f8fafc] py-14">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl font-bold text-[#0f172a] mb-8 text-center">
            Andere diensten van IMPERIA AI
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((d) => {
              const DIcon = d.icon;
              return (
                <Link
                  key={d.id}
                  to={d.route}
                  className="group bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex items-start gap-3"
                >
                  <div className="w-10 h-10 bg-[#D4A017]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DIcon className="w-5 h-5 text-[#D4A017]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0f172a] group-hover:text-[#D4A017] transition-colors text-sm">{d.title}</p>
                    <p className="text-xs text-[#64748b] mt-1 line-clamp-2">{d.shortDesc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#64748b] group-hover:text-[#D4A017] flex-shrink-0 mt-0.5 ml-auto" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
